import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { createFluidHandler } from 'owa-controls-content-handler';
import { DarkModeHandler } from 'owa-controls-content-handler-dark-mode';
import { getPaletteAsRawColors } from 'owa-theme';
import { FluidOwaSource } from 'owa-fluid-validations';
import loc from 'owa-localize';
import {
    collabSpaceNameWhileLoading,
    disableCollabSpace,
} from '../components/CollabSpace.locstring.json';
import { assertNever } from 'owa-assert';
import { showErrorInfo, hasAccessIssue } from '../utils/collabSpaceErrorCheck';
import ErrorCallout from './ErrorCallout';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { logUsage } from 'owa-analytics';
import * as trace from 'owa-trace';
import { ControlIcons } from 'owa-control-icons';
import {
    setIsCollabAllowed,
    setIsCollabSpaceExplicitlyDisabled,
} from '../mutators/collabSpaceMutators';
import { CommandProps } from '@fluidx/office-fluid-types';
import { getCollabSpaceActionMenuItems } from '../utils/getCollabSpaceActionMenuItems';
import { isEventFromDifferentMailbox } from '../utils/isEventFromDifferentMailbox';
import { getViewStateById } from '../selectors/collabSpaceSelectors';
import { IconButton } from '@fluentui/react/lib/Button';
import { onMount } from '../utils/collabSpaceFluidUtils';

const classNames = require('classnames');

interface CollabSpaceRendererProps {
    event: CalendarEvent;
    fluidOwaSource: FluidOwaSource;
    viewStateId: string;
}

let fluidHandler = null;
let darkModeHandler = null;

const SHARELINK_ELEMENT_ID = 'CollabSpaceShareLink';
const styles = require('./CollabSpaceRenderer.scss');

export default observer(function CollabSpaceRenderer(props: CollabSpaceRendererProps) {
    const { event, fluidOwaSource, viewStateId } = props;
    const { fileName, shareLink } = getViewStateById(viewStateId);

    const showErrorCallout = showErrorInfo(viewStateId);
    const eventRef = React.useRef(event);
    const paletteWhite = getPaletteAsRawColors().white;
    const getPaletteWhite = React.useCallback(() => getPaletteAsRawColors().white, []);

    let controlNode: React.ReactElement<{}>;
    let customMenuItems: () => CommandProps[];
    if (fluidOwaSource === FluidOwaSource.CalendarCompose) {
        controlNode = getCollabSpaceControlNode(event, viewStateId);
        customMenuItems = () => {
            return getCollabSpaceActionMenuItems(event, fluidOwaSource, viewStateId);
        };
    }

    const onMountCollabSpace = (loadedInfo: any) => {
        // loadedInfo is typed as 'any' since any references to office-web-host should only be in owa-fluid-integrations
        // Using a ref as event is memoized when onMount is passed as a callback to createFluidHandler
        const currentEvent = eventRef.current;
        onMount(loadedInfo, currentEvent, viewStateId, false /* isNewCollabSpace */);
    };

    React.useEffect(() => {
        return () => {
            const element = document.getElementById(SHARELINK_ELEMENT_ID);
            fluidHandler.undoHandler([element]);
            fluidHandler = null;
            darkModeHandler = null;
        };
    }, []);

    React.useEffect(() => {
        eventRef.current = event;
        fluidHandler =
            fluidHandler ??
            createFluidHandler(
                fluidOwaSource,
                onMountCollabSpace,
                controlNode,
                customMenuItems,
                viewStateId
            );
        darkModeHandler = darkModeHandler ?? new DarkModeHandler(getPaletteWhite);

        if (!hasAccessIssue()) {
            const element = document.getElementById(SHARELINK_ELEMENT_ID);
            if (element) {
                // We first call the undo handler because we want to get rid of existing fluid doc (if present) before hydrating a new one.
                // Applicable for scenarios like removing occurence fluid file to bring back series file in Calendar Compose.
                fluidHandler.undoHandler([element]);
                darkModeHandler.undoHandler(element);

                fluidHandler.handler(element);
                darkModeHandler.handler(element);
            }
        }
    }, [shareLink, paletteWhite, getPaletteWhite]);

    React.useEffect(() => {
        if (showErrorCallout) {
            const viewState = getViewStateById(viewStateId);
            logUsage(
                'ShowCollabSpaceErrorCallout',
                {
                    hasAccessIssue: hasAccessIssue(),
                    fluidLinkFailed: viewState?.fluidLinkFailed,
                    meetingsFolderAccessFailed: viewState?.meetingsFolderAccessFailed,
                },
                { isCore: true }
            );
        }
    }, [showErrorCallout]);

    const logNavigation = () => {
        logUsage('CollabSpaceLinkClicked', undefined, { isCore: true });
    };

    return (
        <div className={getCollabSpaceStyle(fluidOwaSource, showErrorCallout)}>
            <div
                className={
                    showErrorCallout ? styles.linkContainerStyle : styles.fluidContainerStyle
                }>
                <a
                    id={SHARELINK_ELEMENT_ID}
                    href={shareLink}
                    target={'_blank'}
                    onClick={logNavigation}>
                    {fileName ?? loc(collabSpaceNameWhileLoading)}
                </a>
            </div>
            {showErrorCallout && <ErrorCallout viewStateId={viewStateId} />}
        </div>
    );
});

function getCollabSpaceStyle(fluidOwaSource: FluidOwaSource, showErrorCallout: boolean) {
    switch (fluidOwaSource) {
        case FluidOwaSource.CalendarReadingPane:
            return classNames(
                styles.collabSpaceReadingPane,
                showErrorCallout ? styles.collabSpaceReadingPaneError : undefined
            );
        case FluidOwaSource.MailCalendarCard:
            return showErrorCallout ? styles.collabSpaceMailError : styles.collabSpaceMail;
        case FluidOwaSource.CalendarCompose:
            return classNames(
                showErrorCallout
                    ? styles.collabSpaceCalendarComposeError
                    : styles.collabSpaceCalendarCompose
            );
        case FluidOwaSource.MailReadingPane:
        case FluidOwaSource.Timestream:
        case FluidOwaSource.MailCompose:
        case FluidOwaSource.AgendaEditor:
        case FluidOwaSource.None:
            trace.errorThatWillCauseAlert(
                `Incompatible fluid source for getCollabSpaceStyle. Source is ${fluidOwaSource}`
            );
            return null;
        default:
            return assertNever(fluidOwaSource as never);
    }
}

export function getCollabSpaceControlNode(event: CalendarEvent, viewStateId: string) {
    const removeCollabSpace = () => {
        setIsCollabAllowed(false, viewStateId);
        setIsCollabSpaceExplicitlyDisabled(true, viewStateId);
    };

    if (isEventFromDifferentMailbox(event)) {
        return null;
    }

    return (
        <IconButton
            iconProps={{
                iconName: ControlIcons.ChromeClose,
                className: styles.controlIcon,
            }}
            onClick={removeCollabSpace}
            title={loc(disableCollabSpace)}
            className={styles.controlIconContainer}
        />
    );
}
